import { useState, useEffect, useRef } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Header } from './Header';
import { configApi } from '../../api/configApi';
import { useEditorActivityBeacon } from '../../hooks/useEditorActivityBeacon';

export function Layout() {
  const location = useLocation();
  // Feature 015 — record editor page visits (no-op for non-editors).
  useEditorActivityBeacon();
  // The table SESSIONS: the surfaces built on components/table-session/, which get the phone
  // treatment the landscape CSS block in index.css depends on. Add a new session's route prefix
  // here and it inherits that whole layout — the class is the only thing it needs.
  //
  // /problems joined the list once its chrome moved inside the frame (feature 023): the difficulty
  // range now rides in TableFrame's `controls` slot, End Session collapses to the shared brass exit
  // tab, and the status line gives way to the compact masthead row below md. While that chrome sat
  // above and below the frame on the page background, a 303px-tall viewport had nothing left for
  // the three-panel row — which is why this list held one route for as long as it did.
  //
  // /conceptual-problems/<category> joined it in feature 029, when the client asked for the
  // practice page's theme on that surface. Only the BROWSING page qualifies, hence the trailing
  // slash: the category list above it is an ordinary paper page with no frame to lock.
  const TABLE_SESSION_ROUTES = [
    '/module-practice/',
    '/problems',
    '/counting-practice',
    '/conceptual-problems/',
  ];
  const isTableSessionPage = TABLE_SESSION_ROUTES.some((prefix) =>
    location.pathname.startsWith(prefix),
  );
  // Every one of them hosts the same table frame and gets the same app-like, viewport-locked
  // treatment: compact header math, suppressed footer, landscape overlay. The two sets are the same
  // routes now; the distinction is kept because `problems-page` (the height lock) is what a frame
  // needs, while `table-session-page` (the landscape three-panel block) is what a phone-treated
  // session needs, and a future surface could want the first without the second.
  const isTableFramePage = isTableSessionPage;
  // Feature 028: the rebuilt Counting page is a table-session route (below) and no longer wants
  // the pre-028 `counting-page` treatment. /howard-hands is now its only consumer — it keeps the
  // pre-023 styling under its own named exemption.
  const isCountingPage = location.pathname === '/howard-hands';
  // Feature 028: landscape phones are blocked on the REBUILT page too. The play table did not get
  // smaller by moving into the frame — two hands, a trick, a board, a trick count and an auction
  // do not fit 390px of height, which is why the pre-028 page blocked every landscape phone
  // (h <= 550) rather than only the ones the general overlay covers.
  const isCountingSessionRoute =
    location.pathname === '/counting-practice' || location.pathname === '/howard-hands';
  // Feature 021 — the Learn book-chapter reader is a viewport-locked, paginated page that renders
  // its OWN sticky header/footer (Back/Download/title + copyright). Suppress the global footer so
  // the copyright isn't duplicated and the reader can fill the space below the site Header. The
  // landscape-unsupported overlay is intentionally NOT applied here (FR-017 reading exemption).
  const isChapterPage = location.pathname.startsWith('/learn/');
  // Spec 031: the session summary is held to the viewport so its difficulty breakdown, rather
  // than the page, scrolls when the card is taller than the screen, and it shares the compact
  // phone header bands the table pages use (index.css, `session-summary-page`).
  const isSessionSummaryPage = location.pathname === '/session-summary';

  // Dev-only: Viewport size on landscape unsupported overlay (controlled by server config ShowViewportSize)
  const [showVpSize, setShowVpSize] = useState(false);
  const [vpSize, setVpSize] = useState({ w: window.innerWidth, h: window.innerHeight });
  const vpConfigFetched = useRef(false);

  // JS-driven landscape unsupported overlay visibility (replaces CSS media queries)
  // Reacts to dynamic viewport changes (address bar hide/show) via visualViewport API
  const [isLandscapeUnsupported, setIsLandscapeUnsupported] = useState(false);

  useEffect(() => {
    if (vpConfigFetched.current) return;
    vpConfigFetched.current = true;
    configApi.getClientConfig().then(cfg => {
      if (cfg.showViewportSize) setShowVpSize(true);
    }).catch(() => { /* default off */ });
  }, []);

  // For counting page: ALL phone landscape is unsupported (too cramped)
  const [isCountingLandscapeBlocked, setIsCountingLandscapeBlocked] = useState(false);

  // Footer suppression for the in-scope viewport-locked pages. Those pages are designed to
  // fill the entire viewport; rendering the footer at any viewport size pushes their bottom
  // controls past the viewport ceiling and breaks the no-overflow guarantee asserted by
  // edge-case-problems.spec.ts.
  // FR-017's "two-click reachability" rule for the Accessibility link is
  // preserved transitively on these pages: the desktop Header has nav
  // links to non-locked pages whose footer carries the link, and the
  // mobile Header's hamburger menu carries the link directly.
  //
  // The table-frame pages (/problems + module practice) are no longer in that set outright
  // (client report, 2026-07-28: the copyright line was missing altogether on desktop). They
  // render the footer under `.locked-page-footer`, which reveals it only on viewports with the
  // room to spare and, in the SAME breakpoint, hands the frame's height lock back exactly the
  // footer's height — see index.css. Counting Practice and the chapter reader are unchanged:
  // the reader draws its own copyright line, and counting practice has not been re-measured.
  //
  // The REBUILT counting page (feature 028) belongs in this set too, and rejoining it is a fix
  // rather than a preference. Before 028 it was suppressed here as `/counting-practice`; the
  // rebuild moved the route into TABLE_SESSION_ROUTES and narrowed `isCountingPage` to
  // /howard-hands, which silently opted the page into `.locked-page-footer` — a treatment
  // measured for /problems, whose hand panel is ~110px shorter than a play table. The frame's
  // height lock gives up the footer's 3.25rem in that band, and on this page those 52px are
  // the ones the status line was standing in: at 1280x720 in the answered state the score
  // breakdown was clipped by the frame's `overflow: hidden` (measured 2026-09-03, 60px past
  // the frame's bottom edge). The lock's matching giveback is excluded in index.css via the
  // `counting-session` class below — the two rules move together, as that comment says.
  const suppressFooter = isCountingSessionRoute || isChapterPage;

  useEffect(() => {
    const checkViewport = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const isLandscape = w > h;
      // The overlay covers small landscape viewports up to 950 wide, minus the supported
      // 700-950 x 300-550 phone band. Nothing wider than 950 is ever overlaid: since feature
      // 031 the 951-1023 landscape range is served too — the phone row up to 500 tall, the
      // desktop row from 501 (tailwind.config.js `narrow` / `desk-row`) — with no change here.
      const isSmallLandscape = isLandscape && h <= 550 && w <= 950;
      const isSupportedRange = isLandscape && w >= 700 && w <= 950 && h >= 300 && h <= 550;
      setIsLandscapeUnsupported(isSmallLandscape && !isSupportedRange);
      // Counting page: block ALL phone landscape (h <= 550)
      setIsCountingLandscapeBlocked(isLandscape && h <= 550);
      if (showVpSize) setVpSize({ w, h });
    };
    checkViewport();
    window.addEventListener('resize', checkViewport);
    window.visualViewport?.addEventListener('resize', checkViewport);
    return () => {
      window.removeEventListener('resize', checkViewport);
      window.visualViewport?.removeEventListener('resize', checkViewport);
    };
  }, [showVpSize]);

  return (
    // `problems-page` locks the frame to the viewport; `table-session-page` adds the phone
    // treatment on top of it. See TABLE_SESSION_ROUTES above.
    <div className={`min-h-screen flex flex-col${isTableFramePage ? ' problems-page' : ''}${isTableSessionPage ? ' table-session-page' : ''}${isCountingPage ? ' counting-page' : ''}${isCountingSessionRoute ? ' counting-session' : ''}${isSessionSummaryPage ? ' session-summary-page' : ''}`}>
      <a
        href="#main-content"
        className="skip-link"
        onClick={() => document.getElementById('main-content')?.focus()}
      >
        Skip to main content
      </a>
      {((isLandscapeUnsupported && isTableFramePage) || (isCountingLandscapeBlocked && isCountingSessionRoute)) && (
        <div className="landscape-unsupported-overlay" role="alert">
          {showVpSize && <div aria-hidden="true" className="absolute top-2 left-2 text-xs font-bold text-red-700 font-mono">{vpSize.w} x {vpSize.h}</div>}
          <div>
            <p className="text-lg font-semibold text-ink mb-2">Landscape Not Supported</p>
            <p className="text-ink-soft">Please rotate your device to portrait mode for the best experience.</p>
          </div>
        </div>
      )}
      <Header />
      <main id="main-content" tabIndex={-1} className="flex-1 focus:outline-none">
        <Outlet />
      </main>
      {/* Footer matches the masthead: page background, partitioned by the brass
          rule rather than by a grey slab of its own. */}
      {/* The session summary takes the table pages' footer rule too (client request,
          2026-10-05): shown only from 1024x700 up, where there is room for it. On a phone or
          a portrait tablet its line was height the card's difficulty breakdown needed. It needs
          no matching giveback: the summary's lock is a flex column, which takes the room back
          by itself. The hamburger menu carries the Accessibility link where the footer is gone. */}
      {!suppressFooter && (
        <footer
          className={`bg-paper masthead-rule-top py-2 md:py-4 text-center text-sm text-ink-soft${
            isTableFramePage || isSessionSummaryPage ? ' locked-page-footer' : ''
          }`}
        >
          <p>
            &copy; {new Date().getFullYear()} Hawkes Bridge. All rights reserved.
            {' · '}
            <Link to="/accessibility" className="text-primary-700 underline hover:text-primary-900">
              Accessibility
            </Link>
          </p>
        </footer>
      )}
    </div>
  );
}
